/**
 * Supabase client for browser-side operations
 * Used for uploading images and reading gallery data
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

/* =============================
   Types for the `runs` table
   (Adjust to match your schema)
   ============================= */

export type RunImage = {
  angle: string;
  url: string;
  vendor: string;
  seedUsed?: number;
};

export type RunsRow = {
  id: string;
  project_name: string;
  category: string;
  spec: unknown;
  images: RunImage[];
  variants?: unknown[];        // optional; add shape if you persist variants
  policy_version: string;
  created_at: string;          // ISO string
};

export type RunsInsert = RunsRow; // if insert shape == row shape

/* =============================
   Supabase client (browser)
   ============================= */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

let supabaseClient: SupabaseClient | null = null;

export function getSupabaseClient(): SupabaseClient | null {
  if (!supabaseClient && SUPABASE_URL && SUPABASE_ANON_KEY) {
    supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  }
  return supabaseClient;
}

/* =============================
   Storage: upload helper
   ============================= */

export async function uploadImageToSupabase(
  file: Blob,
  filename: string,
  bucket: string = "renders",
): Promise<string | null> {
  const client = getSupabaseClient();
  if (!client) {
    console.warn("Supabase not configured");
    return null;
  }

  try {
    const { error } = await client.storage
      .from(bucket)
      .upload(filename, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) throw error;

    // Get public URL
    const {
      data: { publicUrl },
    } = client.storage.from(bucket).getPublicUrl(filename);

    return publicUrl;
  } catch (error) {
    console.error("Image upload failed:", error);
    return null;
  }
}

/* =============================
   Runs: insert + fetch helpers
   ============================= */

export async function saveRunMetadata(runData: {
  id: string;
  projectName: string;
  category: string;
  spec: unknown;
  images: Array<{ angle: string; url: string; vendor: string }>;
  policyVersion: string;
  createdAt: string;
}) {
  const client = getSupabaseClient();
  if (!client) {
    console.warn("Supabase not configured");
    return null;
  }

  // Map to DB column names
  const payload: RunsInsert = {
    id: runData.id,
    project_name: runData.projectName,
    category: runData.category,
    spec: runData.spec,
    images: runData.images,
    policy_version: runData.policyVersion,
    created_at: runData.createdAt,
  };

  try {
    // Without generated Database types, supabase-js infers `never` here.
    // We pass a typed array and cast to `any` to satisfy the generic.
    const { data, error } = await client
      .from("runs")
      .insert([payload] as any);

    if (error) throw error;
    return data as RunsRow[] | null;
  } catch (error) {
    console.error("Failed to save run metadata:", error);
    return null;
  }
}

export async function fetchRunData(runId: string) {
  const client = getSupabaseClient();
  if (!client) {
    console.warn("Supabase not configured");
    return null;
  }

  try {
    const { data, error } = await client
      .from("runs")
      .select("*")
      .eq("id", runId)
      .single();

    if (error) throw error;
    return data as RunsRow | null;
  } catch (error) {
    console.error("Failed to fetch run data:", error);
    return null;
  }
}
